/*
 * Project: Dynamedics Portal Web
 * Created Date: Wednesday December 20th 2023
 * Author: KasunKarunasekara
 * -----
 * Last Modified: Wednesday December 20th 2023 2:11:08 pm
 * Modified By: KasunKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2023 Mitra Sparks
 * -----
 * HISTORY:
 */

import { Box, Grid, TextField, useTheme } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SwitchSelector from 'react-switch-selector';
import {
  setDashboardDateOption,
  setDashboardDateRanges,
  setDashboardPreviousDateRanges
} from 'src/redux/slices/report';
import { DASHBOARD_DATE_OPTIONS } from 'src/utils/constants';
import {
  getDashboardDateRange,
  getMonthAbbreviation,
  getPreviousDateRange,
  getYearDifferences
} from 'src/utils/functions';
import CustomMonthPicker from './CustomMonthPicker';
import MonthRangePicker from './CustomMonthPicker';
import CustomSwitchSelector from './CustomSwitchSelector';

export default function DashboardDateFilter({}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { selectedDateOption, dashboardDateRanges } = useSelector(
    (state) => state.report
  );
  const { fiscalYearDetails } = useSelector((state) => state.customCalendar);

  const [selectedOption, setSelectedOption] = useState(
    selectedDateOption || '1y'
  );
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    if (selectedDateOption != 'custom') {
      const selectedDateRange = getDashboardDateRange(
        selectedDateOption,
        fiscalYearDetails
      );
      const previousDateRange = getPreviousDateRange(
        selectedDateOption,
        fiscalYearDetails
      );
      setSelectedOption(selectedDateOption);
      dispatch(setDashboardDateRanges(selectedDateRange));
      dispatch(setDashboardPreviousDateRanges(previousDateRange));
    }
  }, [selectedDateOption, fiscalYearDetails]);

  const handleOptionChange = (value) => {
    // Update the state when the option is changed
    setSelectedOption(value);
    dispatch(setDashboardDateOption(value));
  };

  const handleCustomDatePicker = (startDateObj, endDateObj) => {
    const startDate = new Date(
      Date.UTC(startDateObj.year, startDateObj.month - 1, 1, 0, 0, 0)
    );
    const endDate = new Date(
      Date.UTC(endDateObj.year, endDateObj.month, 0, 0, 0, 0)
    );

    const dateLabel =
      startDate.getMonth() == endDate.getMonth() &&
      startDate.getFullYear() == endDate.getFullYear()
        ? `${getMonthAbbreviation(
            startDate.getMonth()
          )} ${startDate.getFullYear()}`
        : `${getMonthAbbreviation(
            startDate.getMonth()
          )} ${startDate.getFullYear()} - ${getMonthAbbreviation(
            endDate.getMonth()
          )} ${endDate.getFullYear()}`;

    const yearDifference = endDate?.getFullYear() - startDate?.getFullYear();

    let prevStartDate = startDate;
    let prevEndDate = endDate;

    // prevStartDate = new Date(prevStartDate.getTime());
    // prevStartDate.setUTCFullYear(prevEndDate.getUTCFullYear() - yearDifference);

    // prevEndDate = new Date(prevEndDate.getTime());
    // prevEndDate.setUTCFullYear(prevEndDate.getUTCFullYear() - yearDifference);

    prevStartDate = new Date(
      Date.UTC(
        prevStartDate.getFullYear() - (yearDifference + 1),
        prevStartDate.getMonth(),
        prevStartDate.getDate(),
        0,
        0,
        0
      )
    );
    prevEndDate = new Date(
      Date.UTC(
        prevEndDate.getFullYear() - (yearDifference + 1),
        prevEndDate.getMonth(),
        prevEndDate.getDate()
      )
    );

    // prevEndDate = new Date(prevEndDate.getTime() - 23 * 60 * 60 * 1000);

    const prevDateLabel =
      prevStartDate.getMonth() == prevEndDate.getMonth() &&
      prevStartDate.getFullYear() == prevEndDate.getFullYear()
        ? `${getMonthAbbreviation(
            prevStartDate.getMonth()
          )} ${prevStartDate.getFullYear()}`
        : `${getMonthAbbreviation(
            prevStartDate.getMonth()
          )} ${prevStartDate.getFullYear()} - ${getMonthAbbreviation(
            prevEndDate.getMonth()
          )} ${prevEndDate.getFullYear()}`;

    if (startDate > endDate) {
      setDateError('The end date can not be before the start date');
    } else {
      setDateError(null);
      handleOptionChange('custom');

      dispatch(setDashboardDateRanges({ startDate, endDate, dateLabel }));
      dispatch(
        setDashboardPreviousDateRanges({
          prevStartDate,
          prevEndDate,
          prevDateLabel
        })
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: 25 }}>
      {/* <SwitchSelector
        options={DASHBOARD_DATE_OPTIONS}
        onChange={handleOptionChange}
        value={selectedOption}
        initialSelectedIndex={DASHBOARD_DATE_OPTIONS?.findIndex(
          (option) => option?.value == selectedDateOption
        )}
        selectedBackgroundColor={theme.palette.common.white}
        selectedFontColor={theme.palette.common.black}
        fontColor={theme.palette.common.white}
        backgroundColor={'#00B1AA'}
      /> */}
      <CustomSwitchSelector
        options={DASHBOARD_DATE_OPTIONS}
        onChange={handleOptionChange}
        value={selectedOption}
        initialSelectedIndex={DASHBOARD_DATE_OPTIONS?.findIndex(
          (option) => option?.value === selectedDateOption
        )}
        selectedBackgroundColor={theme.palette.common.white}
        selectedFontColor={theme.palette.common.black}
        fontColor={theme.palette.common.white}
        backgroundColor={'#00B1AA'}
      />
      <MonthRangePicker
        startYear={new Date()?.getFullYear() - 1}
        endYear={new Date()?.getFullYear()}
        onChange={(startDate, endDate) => {
          handleCustomDatePicker(startDate, endDate);
        }}
        error={dateError}
      />
    </Box>
  );
}
